import React from "react";

// Each visual is a compact glowing SVG matching the x-ray/cyan aesthetic.
// They are pure SVG components — no dependencies. ~140x100 viewBox.

const STROKE = "rgba(34,211,238,0.9)";
const STROKE_SOFT = "rgba(34,211,238,0.45)";
const STROKE_FAINT = "rgba(34,211,238,0.22)";
const AMBER = "rgba(245,158,11,0.85)";
const WHITE_FADE = "rgba(255,255,255,0.85)";

const wrapStyle = {
  filter: "drop-shadow(0 0 6px rgba(34,211,238,0.45)) drop-shadow(0 0 14px rgba(34,211,238,0.18))",
};

function Frame({ children, label }) {
  return (
    <svg viewBox="0 0 140 100" className="w-full h-full" aria-label={label} style={wrapStyle}>
      <defs>
        <radialGradient id="iv-bg" cx="50%" cy="55%" r="65%">
          <stop offset="0%" stopColor="rgba(34,211,238,0.10)" />
          <stop offset="100%" stopColor="rgba(34,211,238,0)" />
        </radialGradient>
      </defs>
      <rect x="0" y="0" width="140" height="100" fill="url(#iv-bg)" rx="14" />
      {children}
    </svg>
  );
}

/* ---------- STEP 1 — Awareness ---------- */

// "Pain is rarely just physical" — mind ↔ body link
export function MindBodyLink() {
  return (
    <Frame label="mind body link">
      {/* head silhouette */}
      <g stroke={STROKE} fill="none" strokeWidth="1.2">
        <ellipse cx="46" cy="34" rx="14" ry="16" />
        <path d="M 40 30 Q 46 24 52 30" stroke={STROKE_SOFT} strokeWidth="0.9" />
      </g>
      {/* body silhouette */}
      <g stroke={STROKE} fill="none" strokeWidth="1.2">
        <path d="M 86 26 Q 98 26 102 36 L 108 70 Q 108 78 100 80 L 80 80 Q 72 78 72 70 L 76 36 Q 80 26 86 26 Z" />
      </g>
      {/* glowing energy stream connecting head to torso */}
      <g fill="none" strokeWidth="1.3">
        <path d="M 60 36 Q 70 30 84 40" stroke={AMBER} strokeLinecap="round" />
        <path d="M 60 40 Q 72 38 84 48" stroke={STROKE} strokeLinecap="round" strokeOpacity="0.7" />
        <path d="M 60 44 Q 70 48 84 56" stroke={STROKE_SOFT} strokeLinecap="round" />
      </g>
      {/* particles in body */}
      {[[88,52],[96,62],[92,72]].map(([x,y])=>(
        <circle key={`${x}-${y}`} cx={x} cy={y} r="1.6" fill={AMBER} />
      ))}
    </Frame>
  );
}

// "Nervous system stores what we cannot say" — silhouette with trapped particles
export function StoredTension() {
  return (
    <Frame label="stored tension">
      <g stroke={STROKE} fill="none" strokeWidth="1.2">
        {/* outline */}
        <path d="M 70 14 Q 84 14 84 28 L 84 36 Q 96 38 96 56 L 92 78 Q 92 86 84 86 L 56 86 Q 48 86 48 78 L 44 56 Q 44 38 56 36 L 56 28 Q 56 14 70 14 Z" />
        {/* spine line */}
        <line x1="70" y1="38" x2="70" y2="80" stroke={STROKE_SOFT} strokeWidth="1" strokeDasharray="2 2" />
      </g>
      {/* trapped emotional particles */}
      <g>
        {[[58,48,1.6],[80,52,2.2],[64,62,1.4],[78,68,1.8],[60,72,1.4],[82,42,1.4]].map(([x,y,r],i) => (
          <circle key={`${x}-${y}`} cx={x} cy={y} r={r} fill={i % 2 ? AMBER : STROKE} opacity="0.85">
            <animate attributeName="opacity" values="0.4;1;0.4" dur={`${2.5 + i*0.3}s`} repeatCount="indefinite" />
          </circle>
        ))}
      </g>
      {/* mute lines indicating "unsaid" */}
      <g stroke={STROKE_FAINT} strokeWidth="0.8">
        <line x1="22" y1="50" x2="36" y2="50" />
        <line x1="22" y1="56" x2="34" y2="56" />
        <line x1="104" y1="50" x2="118" y2="50" />
        <line x1="106" y1="56" x2="118" y2="56" />
      </g>
    </Frame>
  );
}

// "Awareness is the first reset" — opening eye / iris with rays
export function AwarenessEye() {
  return (
    <Frame label="awareness">
      {/* eye outline */}
      <g stroke={STROKE} fill="none" strokeWidth="1.4">
        <path d="M 24 50 Q 70 18 116 50 Q 70 82 24 50 Z" />
      </g>
      {/* iris */}
      <circle cx="70" cy="50" r="14" fill="rgba(34,211,238,0.08)" stroke={STROKE} strokeWidth="1.2" />
      <circle cx="70" cy="50" r="5" fill={AMBER} opacity="0.95" />
      <circle cx="68" cy="48" r="1.5" fill={WHITE_FADE} />
      {/* radiating light rays */}
      <g stroke={STROKE_SOFT} strokeWidth="0.9" strokeLinecap="round">
        {Array.from({length:12}).map((_,i)=>{
          const a = (i/12)*Math.PI*2;
          const x1 = 70 + Math.cos(a)*22;
          const y1 = 50 + Math.sin(a)*22;
          const x2 = 70 + Math.cos(a)*30;
          const y2 = 50 + Math.sin(a)*30;
          return <line key={`ray-${i}`} x1={x1} y1={y1} x2={x2} y2={y2} />;
        })}
      </g>
    </Frame>
  );
}

/* ---------- STEP 2 — Physical ---------- */

// "Posture is emotion frozen in muscle" — spine with crystal/lock
export function FrozenPosture() {
  return (
    <Frame label="frozen posture">
      {/* curved/collapsed spine */}
      <g stroke={STROKE} fill="rgba(34,211,238,0.18)" strokeWidth="1">
        {[18, 28, 38, 48, 58, 68, 78].map((y, i) => {
          const x = 70 + Math.sin(i*0.7) * 6;
          return <rect key={y} x={x-5} y={y} width="10" height="6" rx="2" />;
        })}
      </g>
      {/* crystallized ice motif over upper spine */}
      <g stroke={WHITE_FADE} strokeWidth="0.9" fill="none" strokeLinecap="round">
        <path d="M 100 32 L 116 24 M 100 32 L 116 40 M 100 32 L 108 32" />
        <path d="M 110 28 L 114 24 M 110 28 L 114 32" strokeOpacity="0.6" />
        <path d="M 110 36 L 114 32 M 110 36 L 114 40" strokeOpacity="0.6" />
        <circle cx="100" cy="32" r="2.5" fill={WHITE_FADE} />
      </g>
      {/* tension brackets */}
      <g stroke={AMBER} strokeWidth="1.1" fill="none">
        <path d="M 38 24 L 42 24 L 42 86 L 38 86" />
        <path d="M 102 86 L 98 86 L 98 60 L 102 60" />
      </g>
    </Frame>
  );
}

// "Breath is the bridge to the body" — ribcage with flowing breath
export function BreathBridge() {
  return (
    <Frame label="breath bridge">
      {/* ribcage arcs */}
      <g stroke={STROKE} fill="none" strokeWidth="1.1" strokeLinecap="round">
        {[28, 38, 48, 58, 68].map((y, i) => {
          const sp = 24 - i*1;
          return (
            <g key={y}>
              <path d={`M 70 ${y} Q ${70-sp} ${y+4} ${70-sp-3} ${y+10}`} />
              <path d={`M 70 ${y} Q ${70+sp} ${y+4} ${70+sp+3} ${y+10}`} />
            </g>
          );
        })}
        {/* sternum */}
        <line x1="70" y1="26" x2="70" y2="80" stroke={STROKE_SOFT} strokeWidth="0.8" strokeDasharray="2 2" />
      </g>
      {/* breath wave flowing through */}
      <g fill="none" strokeWidth="1.4" strokeLinecap="round">
        <path d="M 10 50 Q 30 38 50 50 T 90 50 T 130 50" stroke={AMBER} />
        <path d="M 10 60 Q 30 70 50 60 T 90 60 T 130 60" stroke={STROKE} strokeOpacity="0.55" />
      </g>
    </Frame>
  );
}

// "Body adapted — it isn't broken" — bent tree still growing
export function AdaptedTree() {
  return (
    <Frame label="adapted">
      {/* ground */}
      <line x1="14" y1="86" x2="126" y2="86" stroke={STROKE_SOFT} strokeWidth="0.8" strokeDasharray="2 3" />
      {/* trunk bent but reaching up */}
      <g stroke={STROKE} fill="none" strokeWidth="1.6" strokeLinecap="round">
        <path d="M 70 86 Q 56 70 64 50 Q 76 30 70 14" />
      </g>
      {/* roots */}
      <g stroke={STROKE_SOFT} strokeWidth="1" strokeLinecap="round">
        <path d="M 70 86 Q 60 90 50 92" />
        <path d="M 70 86 Q 80 90 90 92" />
        <path d="M 70 86 L 70 96" />
      </g>
      {/* branches with leaves */}
      <g stroke={STROKE} fill="none" strokeWidth="1.1" strokeLinecap="round">
        <path d="M 64 50 Q 50 44 40 36" />
        <path d="M 68 32 Q 84 26 92 18" />
      </g>
      {/* leaves (glowing dots) */}
      {[[40,36],[44,30],[36,42],[92,18],[96,22],[88,12],[70,14]].map(([x,y],i)=>(
        <circle key={`${x}-${y}`} cx={x} cy={y} r="2.2" fill={i % 2 ? STROKE : AMBER} opacity="0.9" />
      ))}
    </Frame>
  );
}

/* ---------- STEP 3 — Mental ---------- */

// "Mind rehearses pain like a song" — loop with waveform
export function PainLoop() {
  return (
    <Frame label="loop">
      {/* loop circle */}
      <g stroke={STROKE} fill="none" strokeWidth="1.4">
        <circle cx="70" cy="50" r="30" />
        {/* arrow tip on the loop */}
        <path d="M 96 36 L 100 40 L 96 44" strokeLinecap="round" strokeLinejoin="round" />
      </g>
      {/* waveform inside */}
      <g stroke={AMBER} fill="none" strokeWidth="1.4" strokeLinecap="round">
        <path d="M 46 50 L 50 50 L 53 38 L 56 62 L 60 44 L 64 56 L 68 46 L 72 54 L 76 42 L 80 58 L 84 50 L 94 50" />
      </g>
      {/* faint repeats */}
      <g stroke={STROKE_FAINT} fill="none" strokeWidth="0.7">
        <circle cx="70" cy="50" r="36" strokeDasharray="2 3" />
        <circle cx="70" cy="50" r="42" strokeDasharray="2 3" />
      </g>
    </Frame>
  );
}

// "Identity is built from repeated thoughts" — concentric rings forming a self
export function IdentityRings() {
  return (
    <Frame label="identity rings">
      {/* concentric rings */}
      <g stroke={STROKE_SOFT} fill="none" strokeWidth="1">
        <circle cx="70" cy="50" r="40" />
        <circle cx="70" cy="50" r="32" strokeOpacity="0.75" />
        <circle cx="70" cy="50" r="24" stroke={STROKE} />
        <circle cx="70" cy="50" r="16" stroke={STROKE} strokeWidth="1.2" />
        <circle cx="70" cy="50" r="9" stroke={STROKE} strokeWidth="1.4" />
      </g>
      {/* inner core */}
      <circle cx="70" cy="50" r="5" fill={AMBER} opacity="0.95" />
      <circle cx="68.5" cy="48.5" r="1.5" fill={WHITE_FADE} />
      {/* orbiting thought-particles */}
      {[[110,50],[30,50],[70,90],[70,10]].map(([x,y])=>(
        <circle key={`${x}-${y}`} cx={x} cy={y} r="1.8" fill={STROKE} opacity="0.85" />
      ))}
    </Frame>
  );
}

// "Neuroplasticity allows any pattern to change" — neural net rewiring
export function NeuralRewire() {
  return (
    <Frame label="neural rewire">
      {/* node positions */}
      {(() => {
        const nodes = [
          [22, 30],[22, 70],
          [56, 22],[56, 50],[56, 78],
          [90, 30],[90, 70],
          [120, 50],
        ];
        const oldEdges = [[0,2],[0,3],[1,3],[1,4],[2,5],[3,5],[3,6],[4,6],[5,7],[6,7]];
        const newPath = [[1,3],[3,5],[5,7]]; // glowing rerouted path
        return (
          <g>
            {/* faded old connections */}
            <g stroke={STROKE_FAINT} strokeWidth="0.7">
              {oldEdges.map(([a,b])=>(
                <line key={`old-${a}-${b}`} x1={nodes[a][0]} y1={nodes[a][1]} x2={nodes[b][0]} y2={nodes[b][1]} />
              ))}
            </g>
            {/* glowing new pathway */}
            <g stroke={AMBER} strokeWidth="1.5" strokeLinecap="round">
              {newPath.map(([a,b])=>(
                <line key={`new-${a}-${b}`} x1={nodes[a][0]} y1={nodes[a][1]} x2={nodes[b][0]} y2={nodes[b][1]} />
              ))}
            </g>
            {/* nodes */}
            {nodes.map(([x,y],i)=>{
              const isNew = newPath.some(([a,b]) => a===i || b===i);
              return (
                <circle key={`${x}-${y}`} cx={x} cy={y} r={isNew ? 3.2 : 2.4} fill={isNew ? AMBER : STROKE} stroke="rgba(255,255,255,0.6)" strokeWidth={isNew ? 0.8 : 0.4} />
              );
            })}
          </g>
        );
      })()}
    </Frame>
  );
}

/* ---------- STEP 4 — Integration ---------- */

// "Not who you were when you arrived" — silhouette transformation
export function TransformedSelf() {
  return (
    <Frame label="transformed">
      {/* faded past silhouette */}
      <g opacity="0.4">
        <path d="M 28 18 Q 38 18 38 28 L 38 34 Q 46 36 46 50 L 44 78 Q 44 86 36 86 L 22 86 Q 14 86 14 78 L 12 50 Q 12 36 20 34 L 20 28 Q 20 18 28 18 Z" fill="none" stroke={STROKE_FAINT} strokeWidth="1.2" strokeDasharray="2 2" />
      </g>
      {/* arrow / transition */}
      <g stroke={AMBER} strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round">
        <path d="M 56 50 L 84 50" />
        <path d="M 78 44 L 84 50 L 78 56" />
      </g>
      {/* future luminous silhouette */}
      <g>
        <defs>
          <radialGradient id="fut" cx="50%" cy="40%" r="70%">
            <stop offset="0%" stopColor="rgba(255,255,255,0.5)" />
            <stop offset="50%" stopColor="rgba(34,211,238,0.4)" />
            <stop offset="100%" stopColor="rgba(34,211,238,0.05)" />
          </radialGradient>
        </defs>
        <path d="M 112 16 Q 124 16 124 26 L 124 34 Q 132 36 132 50 L 130 80 Q 130 88 122 88 L 102 88 Q 94 88 94 80 L 92 50 Q 92 36 100 34 L 100 26 Q 100 16 112 16 Z" fill="url(#fut)" stroke={STROKE} strokeWidth="1.2" />
      </g>
    </Frame>
  );
}

// "Repetition installs the new identity" — DNA helix / spiral
export function RepetitionHelix() {
  return (
    <Frame label="repetition">
      <g stroke={STROKE} fill="none" strokeWidth="1.3" strokeLinecap="round">
        {/* DNA strands */}
        <path d="M 30 10 Q 70 30 110 10 Q 70 50 30 30 Q 70 70 110 50 Q 70 90 30 70 Q 70 110 110 90" />
        <path d="M 110 10 Q 70 30 30 10 Q 70 50 110 30 Q 70 70 30 50 Q 70 90 110 70 Q 70 110 30 90" stroke={STROKE_SOFT} />
      </g>
      {/* rungs */}
      <g stroke={AMBER} strokeWidth="1.4" strokeLinecap="round">
        {[20, 40, 60, 80].map((y,i)=>(
          <line key={y} x1="46" y1={y} x2="94" y2={y} opacity={0.6 + i*0.1} />
        ))}
      </g>
    </Frame>
  );
}

// "Mind, body, emotion operate as one" — 3 overlapping circles converging
export function UnityVenn() {
  return (
    <Frame label="unity">
      <g fill="none" strokeWidth="1.4">
        {/* Mind */}
        <circle cx="54" cy="40" r="22" stroke={STROKE} fill="rgba(34,211,238,0.10)" />
        {/* Body */}
        <circle cx="86" cy="40" r="22" stroke={STROKE} fill="rgba(34,211,238,0.10)" />
        {/* Emotion */}
        <circle cx="70" cy="68" r="22" stroke={STROKE} fill="rgba(34,211,238,0.10)" />
      </g>
      {/* center convergence */}
      <circle cx="70" cy="50" r="6" fill={AMBER} opacity="0.95" />
      <circle cx="70" cy="50" r="11" fill="none" stroke={WHITE_FADE} strokeWidth="0.7" strokeDasharray="2 2" />
      {/* tiny labels */}
      <g fill={STROKE_SOFT} fontSize="6" fontFamily="JetBrains Mono, monospace" textAnchor="middle">
        <text x="40" y="22">MIND</text>
        <text x="100" y="22">BODY</text>
        <text x="70" y="92">EMOTION</text>
      </g>
    </Frame>
  );
}

/* ---------- Map insights → visuals per step ---------- */
export const STEP_INSIGHTS = {
  1: [
    { Visual: MindBodyLink, text: "Pain is rarely just physical." },
    { Visual: StoredTension, text: "The nervous system stores what we cannot say." },
    { Visual: AwarenessEye, text: "Awareness is the first reset." },
  ],
  2: [
    { Visual: FrozenPosture, text: "Posture is emotion frozen in muscle." },
    { Visual: BreathBridge, text: "Breath is the bridge to the body." },
    { Visual: AdaptedTree, text: "The body adapted — it isn't broken." },
  ],
  3: [
    { Visual: PainLoop, text: "The mind rehearses pain like a song." },
    { Visual: IdentityRings, text: "Identity is built from repeated thoughts." },
    { Visual: NeuralRewire, text: "Neuroplasticity allows any pattern to change." },
  ],
  4: [
    { Visual: TransformedSelf, text: "You are not who you were when you arrived." },
    { Visual: RepetitionHelix, text: "Repetition installs the new identity." },
    { Visual: UnityVenn, text: "Mind, body, and emotion now operate as one." },
  ],
};

/* ---------- Extended content for each insight ---------- */
// Used by the clickable detail modal. Keyed by step → array of {body, practices}
// aligned by index with STEP_INSIGHTS.
export const INSIGHT_DETAILS = {
  1: [
    {
      body: "Chronic pain is rarely a pure mechanical problem. The brain takes in physical signals, emotional context, memory, and threat assessment — and produces an output called pain. When emotion goes unprocessed, the brain often translates it into a physical sensation it can locate, name, and protect.",
      mantra: "My pain is information, not a verdict.",
      practices: [
        "When pain spikes, pause and ask: 'What was I feeling 30 seconds before this got louder?'",
        "Keep a 7-day pain–emotion log: time, intensity, and what was happening right before.",
        "Treat each flare as a message, not an attack. Speak to it: 'I hear you.'",
      ],
    },
    {
      body: "Anything you couldn't safely express — grief, anger, fear, exhaustion — does not disappear. It moves into the body and becomes tension, guarding, and posture. Over years, that storage becomes the default shape of how you stand, sleep, and move.",
      mantra: "What I cannot say, my body carries.",
      practices: [
        "Each evening, name one feeling out loud you didn't say earlier today.",
        "Write a private 5-minute letter you'll never send to someone you couldn't speak honestly with.",
        "Notice the area that tenses up first when you read or hear something hard. That's storage.",
      ],
    },
    {
      body: "Awareness alone interrupts an automatic pattern. The moment you notice yourself bracing, scanning for threat, or replaying a thought, the loop loses some of its power. You don't have to fight it — you have to see it.",
      mantra: "Seeing it is changing it.",
      practices: [
        "Set 3 phone reminders today: 'Notice your jaw, shoulders, and breath.'",
        "Name the pattern when you spot it ('there's the old guard'). Naming it weakens it.",
        "End every day with one sentence: 'Today I noticed I…'",
      ],
    },
  ],
  2: [
    {
      body: "Posture is your body's accumulated answer to repeated emotional states. Collapsed chest often follows years of self-protection. Locked shoulders often follow vigilance. Your shape didn't appear randomly — it was built, response by response.",
      mantra: "My posture is a history, not a personality.",
      practices: [
        "Once an hour, lengthen the back of your neck and let your shoulders drop on a long exhale.",
        "Stand against a wall with heels, glutes, upper back, and head touching for 60 seconds.",
        "Notice which posture you collapse into when you read difficult news. That's emotional.",
      ],
    },
    {
      body: "Breath is the one nervous-system input you can control consciously. Slow, low, nasal breaths tell your body the threat is over. Short, high, chest breaths keep you locked in 'protect mode' — and protect mode breeds pain.",
      mantra: "Breath is the safest reset I always have.",
      practices: [
        "Practice 4–7–8 (inhale 4 · hold 7 · exhale 8) three times before every meal.",
        "When pain spikes, breathe so slowly that the exhale is twice as long as the inhale.",
        "Place a hand on your belly and a hand on your chest. Aim for the belly to move first.",
      ],
    },
    {
      body: "Your body did not betray you. It adapted to what you asked it to survive. The bend in your back, the limp, the tightness — those are intelligent responses to load, fear, and lack of recovery. Adaptation is reversible.",
      mantra: "I am not broken. I am adapted.",
      practices: [
        "Replace 'my back is wrecked' with 'my back has been working hard for me.'",
        "Each morning, thank a part of your body that's been overworking. Out loud.",
        "Pick one movement you've been avoiding because of fear. Try 10% of it today.",
      ],
    },
  ],
  3: [
    {
      body: "The brain rehearses what it expects to need. If it expects pain, it lights up the pain circuit ahead of the stimulus — and the rehearsal itself becomes the experience. Pain that's heavily anticipated tends to arrive on time.",
      mantra: "Anticipation is half the pain.",
      practices: [
        "Catch yourself bracing before an action you fear (sitting, lifting, sleeping). Soften first.",
        "Replace 'this is going to hurt' with 'let's see what shows up' — and breathe.",
        "Watch one neuroscience video on predictive coding. Knowing the mechanism softens it.",
      ],
    },
    {
      body: "Identity is just thoughts you've practiced for years until they felt like 'you'. 'I'm someone with a bad back', 'I can't sit long', 'I'm fragile' — these are rehearsed identities, not facts. Identity changes when the inner sentence changes.",
      mantra: "My identity is editable.",
      practices: [
        "List your three most-said sentences about your body. Rewrite each in the present-tense identity you want.",
        "Repeat the new sentence aloud 5 times in the morning and 5 at night for 14 days.",
        "When the old identity speaks, say internally: 'That's the old voice. I'm listening to the new one.'",
      ],
    },
    {
      body: "The brain rewires through attention and repetition. Every time you respond to a familiar trigger in a new way, you weaken the old neural pathway and strengthen a new one. Change is not a personality trait — it's a protocol.",
      mantra: "Repetition rewires.",
      practices: [
        "Pick one pain-related habit (clenching, slouching, complaining). Replace it with one specific new response for 30 days.",
        "Celebrate every catch-and-redirect, no matter how small. Cells respond to felt reward.",
        "Track the new pathway in a single note: date + the moment you chose differently.",
      ],
    },
  ],
  4: [
    {
      body: "The person who started this program is not the person reading this now. You have new awareness, new patterns of breath, new permission to feel. The body remembers — but it now remembers a different operating system.",
      mantra: "I am no longer the person who arrived here.",
      practices: [
        "Write a short letter from your current self to your day-one self. Forgive them. Thank them.",
        "List 5 specific things that are different about how you live now.",
        "Keep this letter. Re-read it whenever the old identity tries to return.",
      ],
    },
    {
      body: "Identity is installed through repetition the way music gets stuck in your head. The new posture, the new breath, the new inner sentence — they only become 'you' through daily, almost boring, repetition. Boring is the price of permanent.",
      mantra: "Boring repetition is the magic.",
      practices: [
        "Anchor one Reset practice to something you already do daily (e.g. breath while brewing coffee).",
        "Use the Back Pain Transformer as your physical anchor — wear it during the same ritual each day.",
        "Track 30 days. Don't break the chain.",
      ],
    },
    {
      body: "When mind, body, and emotion stop fighting each other, the energy that was spent on internal conflict becomes available for life. This is the Reset State — not a peak, but a baseline that quietly supports everything else.",
      mantra: "I operate as one.",
      practices: [
        "Each morning, place a hand on your chest and say: 'Mind, body, emotion — we are on the same team today.'",
        "When something hard happens, check all three: 'What is my mind, body, and emotion each saying?'",
        "Trust slow days. Coherence often arrives quietly.",
      ],
    },
  ],
};

/* ---------- Card wrapper ---------- */
import { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogTitle,
} from "./ui/dialog";
import { Sparkles as SparklesIcon, ChevronRight as ChevronRightIcon, Check as CheckIcon, Loader2 as LoaderIcon } from "lucide-react";
import { api } from "../lib/api";

/** Per-practice autosaving note textarea. Saves on blur and when modal closes. */
function PracticeNote({ step, insightIndex, practiceIndex, insightText, practiceText, initialValue = "" }) {
  const [value, setValue] = useState(initialValue);
  const [status, setStatus] = useState("idle"); // idle | saving | saved
  const savedRef = useRef(initialValue);

  useEffect(() => {
    setValue(initialValue);
    savedRef.current = initialValue;
  }, [initialValue]);

  async function save() {
    const trimmed = value.trim();
    if (trimmed === (savedRef.current || "").trim()) return;
    setStatus("saving");
    try {
      await api.post("/insight-notes", {
        step,
        insight_index: insightIndex,
        practice_index: practiceIndex,
        insight_text: insightText,
        practice_text: practiceText,
        text: trimmed,
      });
      savedRef.current = trimmed;
      setStatus("saved");
      setTimeout(() => setStatus("idle"), 1400);
    } catch (e) {
      console.error("Failed to save insight note:", e?.response?.data || e?.message);
      setStatus("idle");
    }
  }

  return (
    <div className="mt-2 ml-9">
      <div className="relative">
        <textarea
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onBlur={save}
          placeholder="Write what came up for you…"
          rows={2}
          data-testid={`insight-note-${insightIndex + 1}-${practiceIndex + 1}`}
          className="w-full rounded-xl bg-[#03101F]/70 border border-white/10 focus:border-[#22D3EE]/50 focus:outline-none focus:ring-2 focus:ring-[#22D3EE]/15 px-3 py-2 text-sm text-white placeholder:text-[#5A6E87]/70 leading-relaxed resize-none transition-all"
        />
        <div className="absolute right-2 bottom-1.5 text-[10px] font-mono uppercase tracking-[0.18em] text-[#7C8FA8] flex items-center gap-1 pointer-events-none">
          {status === "saving" && (<><LoaderIcon size={10} className="animate-spin" /> Saving</>)}
          {status === "saved" && (<><CheckIcon size={10} className="text-[#22D3EE]" /> <span className="text-[#22D3EE]">Saved</span></>)}
        </div>
      </div>
    </div>
  );
}

export function InsightCard({ Visual, text, index = 0, detail, step = 1 }) {
  const [open, setOpen] = useState(false);
  const [existingNotes, setExistingNotes] = useState({}); // { practice_index: text }

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    api.get("/insight-notes").then((r) => {
      if (cancelled) return;
      const map = {};
      for (const n of r.data || []) {
        if (n.step === step && n.insight_index === index) {
          map[n.practice_index] = n.text || "";
        }
      }
      setExistingNotes(map);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [open, step, index]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          type="button"
          className="bps-card overflow-hidden flex gap-4 items-center p-4 w-full text-left hover:-translate-y-0.5 hover:border-[#22D3EE]/30 transition-all group"
          data-testid={`insight-card-${index + 1}`}
        >
          <div className="w-24 h-20 shrink-0 rounded-2xl bg-[#03101F]/60 border border-white/5 p-1 flex items-center justify-center">
            <Visual />
          </div>
          <div className="flex-1">
            <div className="bps-eyebrow text-[#22D3EE] mb-1">Insight {index + 1}</div>
            <p className="font-display text-base leading-snug text-white">{text}</p>
            {detail && (
              <div className="mt-1.5 inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-[0.2em] text-[#7C8FA8] group-hover:text-[#22D3EE] transition-colors">
                Tap to explore <ChevronRightIcon size={11} />
              </div>
            )}
          </div>
        </button>
      </DialogTrigger>
      {detail && (
        <DialogContent
          className="border border-[#22D3EE]/25 bg-[#0A1530]/95 backdrop-blur-xl rounded-3xl shadow-[0_24px_60px_rgba(0,0,0,0.6)] max-w-md max-h-[85vh] overflow-y-auto p-0"
          data-testid={`insight-detail-${index + 1}`}
        >
          <div className="p-6 space-y-5">
            <div className="flex items-start gap-4">
              <div className="w-24 h-20 shrink-0 rounded-2xl bg-[#03101F]/70 border border-white/5 p-1 flex items-center justify-center">
                <Visual />
              </div>
              <div className="flex-1">
                <div className="bps-eyebrow text-[#22D3EE] mb-1.5">Insight {index + 1}</div>
                <DialogTitle className="font-display text-xl font-medium tracking-tight text-white leading-snug">{text}</DialogTitle>
              </div>
            </div>

            <p className="text-sm text-white/85 leading-relaxed">{detail.body}</p>

            {detail.mantra && (
              <div className="rounded-2xl border border-[#22D3EE]/30 bg-[#22D3EE]/[0.06] p-4 flex items-start gap-3">
                <SparklesIcon size={16} className="text-[#22D3EE] mt-0.5 flex-shrink-0" />
                <div>
                  <div className="bps-eyebrow text-[#22D3EE] mb-1">Mantra</div>
                  <p className="font-display text-base text-white italic leading-snug">"{detail.mantra}"</p>
                </div>
              </div>
            )}

            <div>
              <div className="bps-eyebrow mb-3 text-[#F59E0B]">Use this on the journey</div>
              <ol className="space-y-4" data-testid="insight-practices">
                {detail.practices.map((p, i) => (
                  <li key={p} className="text-sm">
                    <div className="flex gap-3">
                      <span className="mt-0.5 inline-flex items-center justify-center w-6 h-6 rounded-full border border-[#F59E0B]/55 text-[#F59E0B] font-mono text-[11px] font-semibold flex-shrink-0">{i + 1}</span>
                      <span className="text-white/90 leading-relaxed flex-1">{p}</span>
                    </div>
                    <PracticeNote
                      step={step}
                      insightIndex={index}
                      practiceIndex={i}
                      insightText={text}
                      practiceText={p}
                      initialValue={existingNotes[i] || ""}
                    />
                  </li>
                ))}
              </ol>
              <div className="mt-3 ml-9 text-[10px] font-mono uppercase tracking-[0.2em] text-[#7C8FA8]">
                Your reflections autosave · view all in <span className="text-[#22D3EE]">Settings → Notes</span>
              </div>
            </div>
          </div>
        </DialogContent>
      )}
    </Dialog>
  );
}
